// ============================================================================
// GST MODULE INFO SCREEN — Slice 0 walking skeleton
// ============================================================================
// This screen exists to prove the frontend → API vertical before any GST
// domain work begins: the route resolves, the component registry finds the
// screen, the axios client attaches auth + tenant headers, and the backend's
// entitlement gate answers. Each of those fails differently and silently when
// wired wrongly, so the screen reports on them individually rather than
// rendering "ok".
//
// It is replaced by the GST Filing Dashboard in G-1. Nothing should be built
// on top of it.
//
// Per prima-web/CLAUDE.md Module Boundaries: GST screens live under
// src/components/modules/gst/ and import only from the shared design system —
// never from another module's folder.

import React, { useCallback, useEffect, useState } from 'react';
import { ModuleHeader } from 'components/modals';
import { gstService } from 'api/services/gstService';
import type { GstModuleInfo } from 'api/types/gst.types';

type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; info: GstModuleInfo }
  // 404 from the backend means "not enabled for this tenant", which is a
  // configuration state rather than a fault. Kept distinct from 'error' so the
  // screen does not cry wolf about a deliberate setting.
  | { status: 'not-enabled' }
  | { status: 'error'; message: string };

const GstModuleInfoScreen: React.FC = () => {
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  const load = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      const info = await gstService.getModuleInfo();
      setState({ status: 'ready', info });
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setState({ status: 'not-enabled' });
        return;
      }
      setState({
        status: 'error',
        message: err?.response?.data?.message ?? err?.message ?? 'Unknown error',
      });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const renderCheck = (label: string, ok: boolean, detail?: string) => (
    <div
      key={label}
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 12,
        padding: '8px 0',
        borderBottom: '1px dotted #ddd',
      }}
    >
      <span style={{ minWidth: 20, color: ok ? '#00ab84' : '#c0392b' }}>
        {ok ? '✓' : '✗'}
      </span>
      <span style={{ minWidth: 280 }}>{label}</span>
      <span style={{ color: '#666' }}>{detail}</span>
    </div>
  );

  return (
    <div className="module-screen">
      <ModuleHeader
        title="GST Module"
        description="Slice 0 skeleton — verifies the module is installed and reachable for this tenant"
      />

      <div style={{ padding: 16, maxWidth: 720 }}>
        {state.status === 'loading' && <p>Checking module status…</p>}

        {state.status === 'not-enabled' && (
          <div>
            <p>
              <strong>The GST module is not enabled for this tenant.</strong>
            </p>
            <p style={{ color: '#666' }}>
              Enable it under <em>Administration → Configuration → System
              Defaults → GST Module Enabled</em>. Until then every GST endpoint
              returns 404, which is deliberate: a tenant without the module does
              not learn that it exists.
            </p>
          </div>
        )}

        {state.status === 'error' && (
          <div>
            <p style={{ color: '#c0392b' }}>
              <strong>Could not reach the GST module.</strong>
            </p>
            <p style={{ color: '#666' }}>{state.message}</p>
            <p style={{ color: '#666' }}>
              This is distinct from the module being disabled — that returns 404
              and is reported separately.
            </p>
          </div>
        )}

        {state.status === 'ready' && (
          <div>
            {renderCheck('Module enabled for this tenant', state.info.moduleEnabled)}
            {renderCheck(
              'Route prefix',
              state.info.routePrefix === 'api/v1/gst',
              state.info.routePrefix,
            )}
            {renderCheck(
              'Workflow engine resolves inside the module',
              state.info.workflowEngineResolved,
            )}
            {renderCheck(
              'Authenticated caller reached the module',
              Boolean(state.info.callerUserId),
              state.info.callerUserName ?? '(no name on token)',
            )}
            <p style={{ marginTop: 16, color: '#666' }}>
              Nothing else is built yet. The GST Filing Dashboard replaces this
              screen in phase G-1.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GstModuleInfoScreen;
